import { z } from 'zod';

// How the channel array is joined into the single string the ficha renders
// (e.g. ["Email", "Push"] → "Email y Push").
const COMMS_SEPARATOR = ' y ';

// A «Usuario» (Recipient) returned by the phone search, enriched with the account
// fields the ficha renders. This schema mirrors the shape app-service *delivers* on
// `/users?phone=` (a DRF list item — numeric id, snake_case keys, `location_code`,
// `comms` as a channel array) and `.transform()`s it into the camelCase domain hit the
// panel consumes; the exported `UserSearchHit` type is the transformed (domain) shape.
//
// In the domain hit `id` is the numeric USER ID stringified (rendered + used in URL
// paths). `active` flags whether the account is in force (not blocked, not deleted):
// when a phone unexpectedly resolves to more than one «Usuario», the panel opens the
// first active recipient — it coexists with `status`, the multi-state account status
// the chip shows.
//
// `status` is an open string, not a closed enum: a new backend state must not break
// parsing — the UI maps known states to a colour and degrades the rest to neutral.
// `locationCode` (delivered as `location_code`) is the location code already zero-padded
// by the backend (e.g. DIZ00); the panel renders it verbatim, no padding of its own.
//
// One part of this read contract is NOT yet confirmed by the backend: how an anonymized
// account comes back once it has been given de baja. The panel proposes that the old
// phone keeps resolving the hit, that every PII field arrives null, and that the account
// carries a deletion date and an observations trail under the wire names used below.
// Those three points are a request awaiting backend confirmation, so they are modelled
// defensively — nullish everywhere, normalized to empty strings — and the schema will
// keep parsing whichever of them the delivered shape ends up contradicting. Revisit the
// names and tighten the optionality once the delivered shape is confirmed.
export const UserSearchHitSchema = z
  .object({
    id: z.number(),
    // Empty on the wire for carrier pre-registrations (account created with a phone
    // only), so an empty/null name is a valid hit — it normalizes to '' and the UI
    // falls back, instead of one nameless hit sinking the whole search response.
    name: z.string().nullish(),
    // Dropped its non-empty guard when anonymized accounts appeared: a hit no longer
    // has to echo back the number the search resolved it by.
    phone: z.string().nullish(),
    active: z.boolean(),
    status: z.string(),
    // Enrichment fields are nullish on the wire (the backend may omit or null them);
    // they normalize to empty strings so the ficha renders them as em dashes instead
    // of failing the whole account card on a parse error.
    email: z.string().nullish(),
    signup_date: z.string().nullish(),
    address: z.string().nullish(),
    location_code: z.string().nullish(),
    comms: z.array(z.string()).nullish(),
    // Baja trail — only an anonymized account carries these; a live one omits them.
    // `deletion_date` is date-only ISO; `observations` is machine-generated text the
    // ficha renders inert (never parsed, never used to drive behaviour).
    deletion_date: z.string().nullish(),
    observations: z.string().nullish(),
    // Whether the account has ever received an Amazon Bookings (carrier 4800) delivery as
    // recipient — the backend computes it over full history and ships it on every hit. It gates
    // the visibility of the Amazon-exclusion checkbox in the baja modal (presentation only; the
    // write does not revalidate it). Declared nullish though the delivered contract makes it a
    // required boolean: during the deploy window an older app-service can omit it, and an absent
    // flag must not sink the whole search parse — it fails closed to `false` (no check offered).
    has_amazon_deliveries: z.boolean().nullish(),
    // The account's role as backend delivers it: the id and the name of its row in the roles table,
    // and nothing else from that table — Zod strips any other key, so the minimization holds even if
    // the answer grows. Nullish because an older deploy omits it, and `.catch(null)` because a
    // malformed role must cost the strip on the ficha, not the whole search.
    account_type: z
      .object({ id: z.number().int(), name: z.string().nullish() })
      .nullish()
      .catch(null),
  })
  .transform((hit) => ({
    id: String(hit.id),
    name: hit.name ?? '',
    phone: hit.phone ?? '',
    active: hit.active,
    status: hit.status,
    email: hit.email ?? '',
    signupDate: hit.signup_date ?? '',
    address: hit.address ?? '',
    locationCode: hit.location_code ?? '',
    comms: (hit.comms ?? []).join(COMMS_SEPARATOR),
    deletionDate: hit.deletion_date ?? '',
    observations: hit.observations ?? '',
    hasAmazonDeliveries: hit.has_amazon_deliveries ?? false,
    accountType: hit.account_type
      ? { id: hit.account_type.id, name: hit.account_type.name ?? null }
      : null,
  }));

// The DRF-paginated list envelope app-service returns. The panel reads `results`
// (single phone → at most a handful of hits); the cursor fields are carried for
// fidelity but not yet paged through.
export const UserSearchResponseSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: z.array(UserSearchHitSchema),
});

export type UserSearchHit = z.infer<typeof UserSearchHitSchema>;
export type UserSearchResponse = z.infer<typeof UserSearchResponseSchema>;
